import { CreatorAction, CreatorActionType } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import { Loader } from './Loader';
import { getFileSrc } from '@/utils/utilities';
import { RenderUtils } from './RenderUtils';
import { MapType } from '@/api/AssetApi';
import { AssetsLib } from './AssetsLib';
import { UserDataProp } from '../types';
import {
	MeshStandardMaterial,
	RepeatWrapping,
	SRGBColorSpace,
	LinearSRGBColorSpace,
	Color,
} from 'three';

export class ActionHandler {
	private loader = Loader.getInstance();
	private assetsLib = AssetsLib.getInstance();
	private materialActionsCache: Map<string, CreatorAction> = new Map();

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
	}

	triggerLastAction(target: string) {
		const lastAction = this.materialActionsCache.get(target);

		if (!lastAction) return;
		this.dispatchAction(lastAction, false);
	}

	async dispatchAction(action: CreatorAction, cacheMaterialActions: boolean) {
		switch (action.type) {
			case CreatorActionType.SET_MATERIAL:
				await this.handleMaterialAction(action, cacheMaterialActions);
				break;
			case CreatorActionType.TOGGLE_VISIBILITY:
				this.handleVisibilityAction(action);
				break;
			default:
				throw new Error('UNKNOWN_ACTION_TYPE');
		}
	}

	private async handleMaterialAction(
		action: CreatorAction,
		cacheMaterialActions: boolean
	) {
		const asset = action.payload.asset;
		if (!asset || !action.payload.target) return;

		if (cacheMaterialActions)
			this.materialActionsCache.set(action.payload.target, action);

		const objectTargetName = action.payload.target;

		if (!asset.maps[MapType.BASE]) {
			const material = new MeshStandardMaterial({
				...asset.options,
				color: new Color(asset.options.color || '#ffffff'),
			});

			this.renderer.project.traverse((child) => {
				if (!RenderUtils.isMesh(child)) return;
				if (child.userData.target !== objectTargetName) return;

				child.material = material;
			});

			return;
		}

		const textures = await Promise.all(
			Object.values(MapType).map((type) => {
				if (!asset.maps[type]) return null;

				const cached = this.assetsLib.texturesCache.get(asset.maps[type].path);
				if (cached) return cached;

				return this.loader
					.loadTexture(getFileSrc(asset.maps[type].path))
					.then((texture) => {
						this.assetsLib.texturesCache.set(asset.maps[type]!.path, texture);

						return texture;
					});
			})
		);

		this.renderer.project.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;

			if (
				action.isPostConfiguratorAction &&
				child.userData[UserDataProp.POST_ID] !== objectTargetName
			)
				return;
			if (
				!action.isPostConfiguratorAction &&
				child.userData[UserDataProp.TARGET] !== objectTargetName
			)
				return;

			const displacementScale = child.userData.displacement_scale ?? 0.0005;

			const cacheKey = `${asset.id}_${displacementScale}`;
			let material = this.assetsLib.materialCache.get(cacheKey);

			if (!material) {
				for (const texture of textures) {
					if (!texture) continue;

					texture.wrapS = RepeatWrapping;
					texture.wrapT = RepeatWrapping;
					texture.repeat.set(1, 1);
				}

				const [
					map,
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				] = textures;

				if (!map) throw new Error('MISSING_MAP');

				map.colorSpace = SRGBColorSpace;

				for (const texture of [
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				]) {
					if (!texture) continue;
					texture.colorSpace = LinearSRGBColorSpace;
				}

				material = new MeshStandardMaterial({
					map,
					// normalScale: new Vector2(0.025, 0.025),
					displacementMap,
					displacementScale,
					metalnessMap,
					normalMap,
					roughnessMap,
					aoMap,
					lightMap,
					...asset.options,
				});

				this.assetsLib.materialCache.set(cacheKey, material);
			}

			child.material = material;
		});
	}

	private handleVisibilityAction(action: CreatorAction) {
		if (!action.payload.target && !action.payload.hideTargets) return;

		const hideTargets = action.payload.hideTargets?.split(',') || [];

		this.renderer.project.traverse((child) => {
			if (!child.userData[UserDataProp.VARIANT_NAME]) return;

			if (child.userData[UserDataProp.VARIANT_NAME] === action.payload.target)
				child.visible = true;
			if (hideTargets.includes(child.userData[UserDataProp.VARIANT_NAME]))
				child.visible = false;
		});
	}
}
